import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Badge } from '../ui/badge'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu'

export type PillOption = {
  id: number
  name: string
  /** Second line in the list, e.g. the Arabic name. */
  hint?: string | null
  /** Small tag after the name, in the list AND on the pill — "Required". */
  tag?: string | null
}

/**
 * A field of removable pills that opens a checkbox list.
 *
 * This is the branch picker, generalised. The product editor picked its
 * add-on groups from a bare checkbox list in a box while, one card above it,
 * branches were picked from this — two controls for "choose some of these"
 * on one page, and the second one is the one people already knew. Branches
 * still use it through BranchMultiSelect; add-on groups use it directly.
 */
export function PillMultiSelect({
  options, selectedIds, onChange, disabled, className,
  placeholder, allLabel, emptyText, footer, testid,
}: {
  /** A data-testid for the trigger, for the QA suites — e.g. the product
   *  editor passes data-testid="addon-picker". */
  testid?: string
  options: PillOption[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
  disabled?: boolean
  className?: string
  placeholder?: string
  /** When given, an "all" row sits above the list and, with everything
   *  selected, the field shows this one pill instead of every name. */
  allLabel?: string
  emptyText?: ReactNode
  /** Rendered under the list — a "create one" shortcut, typically. */
  footer?: ReactNode
}) {
  const { t } = useTranslation()
  const allSelected = !!allLabel && options.length > 0 && selectedIds.length === options.length
  const selected = options.filter(o => selectedIds.includes(o.id))

  const toggle = (id: number) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id])
  const toggleAll = () => onChange(allSelected ? [] : options.map(o => o.id))
  const remove = (id: number) => onChange(selectedIds.filter(x => x !== id))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          data-selected-count={selectedIds.length}
          className={cn(
            'flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-3 py-1.5 text-start text-sm shadow-sm transition-colors hover:bg-accent/30 focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          {allSelected ? (
            <Badge variant="secondary" className="text-xs font-medium">{allLabel}</Badge>
          ) : selected.length === 0 ? (
            <span className="text-muted-foreground">{placeholder ?? t('common.select', 'Select…')}</span>
          ) : (
            selected.map(o => (
              <Badge key={o.id} variant="secondary" className="gap-1 text-xs font-normal">
                {o.name}
                {o.tag && <span className="text-[9px] font-bold uppercase text-amber-700 dark:text-amber-300">{o.tag}</span>}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={t('common.remove', 'Remove')}
                  onPointerDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); remove(o.id) }}
                  className="ms-0.5 rounded-full hover:bg-muted-foreground/20"
                >
                  <X className="w-3 h-3" />
                </span>
              </Badge>
            ))
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[--radix-dropdown-menu-trigger-width] max-h-72 overflow-y-auto"
      >
        {options.length === 0 ? (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            {emptyText ?? t('common.noData')}
          </div>
        ) : (
          <>
            {allLabel && (
              <>
                <DropdownMenuCheckboxItem
                  checked={allSelected}
                  onSelect={e => { e.preventDefault(); toggleAll() }}
                  className="font-medium"
                >
                  {allLabel}
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
              </>
            )}
            {options.map(o => (
              <DropdownMenuCheckboxItem
                key={o.id}
                checked={selectedIds.includes(o.id)}
                onSelect={e => { e.preventDefault(); toggle(o.id) }}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-2">
                    <span className="truncate">{o.name}</span>
                    {o.tag && (
                      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300">
                        {o.tag}
                      </span>
                    )}
                  </span>
                  {o.hint && <span className="truncate text-xs text-muted-foreground">{o.hint}</span>}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
        {footer && (
          <>
            <DropdownMenuSeparator />
            <div className="px-1 py-1">{footer}</div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
